// ============================================================================
// src/pig/PigEntry.tsx — the pig domain's nested routes.
// ============================================================================
// B3.1 finish, fifth domain. Replaces resolvePigEntry. See
// src/cattle/CattleEntry.jsx for the pattern and
// src/app/navigation/viewRoutes.js for why `location` is the ROUTED location.
//
// App still passes several historical props while its legacy branch exists, but
// the released pig batch/farrowing mounts no longer receive any of them. Their
// reads, writes, and debounce state are route-local.
// ============================================================================
import React from 'react';
import type {Dispatch, SetStateAction} from 'react';

import {AppRoute, AppRoutes} from '../app/navigation/index.js';
import {PIG_ROUTES} from './pigRoutes.js';

import PigDailysView from './PigDailysView.tsx';
import PigsHomeView from './PigsHomeView.tsx';
import BreedingView from './BreedingView.tsx';
import PigBatchesView from './PigBatchesView.tsx';
import FarrowingView from './FarrowingView.tsx';
import SowsView from './SowsView.tsx';
import PageBoundary from '../app/PageBoundary.tsx';

// /pig/feed loads with its route, as it does in the released renderer: the
// feed ledger and its two confirmed autosaves stay out of the startup chunk.
const PigFeedView = React.lazy(() => import('./PigFeedView.tsx'));

export {PIG_ROUTES};

// Declared here rather than shared with the other four domain entries: they
// have the same shape, not the same module, and a cross-domain import would add
// an edge to the graph the route guards walk.
type RoutedLocationProp = string | {pathname: string; search?: string; hash?: string; state?: unknown};
type RefreshDailys = (kind: string) => Promise<void>;

// Rows out of app_store blobs (ppp-feeders-v1, ppp-breeding-v1, …) and out of
// the pig_dailys table. No declaration in this repo describes their element
// shape and this component only forwards them, so `unknown[]` is the honest
// element type rather than an invented interface.
type RowList = unknown[];

// ppp-pig-feed-inventory: App initialises the state to null (its own comment
// says "{count, date} or null"), so null is a real value, not a placeholder.
type PigFeedInventory = Record<string, unknown> | null;

// The App-owned autosave handles. Every consumer does
// `clearTimeout(t.current)` then `t.current = setTimeout(...)`, so the ref is
// mutable and starts null. Written structurally rather than as
// React.MutableRefObject because that alias is deprecated in newer @types/react
// and this file has no reason to care which one is installed.
type TimerRef = {current: ReturnType<typeof setTimeout> | null};

type PigEntryProps = {
  location: RoutedLocationProp;
  // ── The three /pig/dailys props, kept DECLARED and no longer DESTRUCTURED ──
  //
  // Both pig daily surfaces own their reads now, so none of these reaches a
  // route from here. They stay on the type because App still passes all three
  // in its `React.createElement(PigEntry, {…})` literal and an undeclared prop
  // is an excess-property error at that call site — and App is the integration
  // hub, out of this change's scope. Removing them there is App's own change.
  //
  //   setPigDailys   is `useDailysRecent().setPigDailys`; PigDailysHub calls the
  //                  same setter off the same provider directly.
  //   feederGroups   was App's former-eager ppp-feeders-v1 boot array. Handing
  //                  it down here is exactly the consumption
  //                  tests/static/former_eager_evidence_static.test.js derives,
  //                  so restoring it would make the `pigdailys` retirement in
  //                  tests/fixtures/former-eager-dependencies.json a false
  //                  claim. PigDailysRouter reads it through
  //                  pig.dailys.groupDirectory instead.
  //   refreshDailys  was App's global post-write fan-out; the hub owns its pig
  //                  branch (loadAllPigDailys into that same setter) itself.
  setPigDailys: Dispatch<SetStateAction<RowList>>;
  feederGroups: RowList;
  refreshDailys: RefreshDailys;
  // ── The two /pig/feed BLOBS, kept DECLARED and no longer DESTRUCTURED ──
  //
  // Same posture, and the same reason: App still passes both in its
  // `React.createElement(PigEntry, {…})` literal, so removing them from this
  // type is an excess-property error at that call site and App is the
  // integration hub, out of this change's scope.
  //
  //   feedOrders        ppp-feed-orders-v1, shared with the broiler feed view:
  //                     feed type -> 'YYYY-MM' -> order quantity. PigFeedView
  //                     reads it — and the pig planner blobs, the feed rates and
  //                     the daily history — through pig.feed.core now.
  //   pigFeedInventory  ppp-pig-feed-inventory-v1, the physical count, off the
  //                     same boot app_store transaction and the same resource.
  //
  // Handing either one down here is exactly the consumption
  // tests/static/former_eager_evidence_static.test.js derives, so restoring it
  // would make the `pigs` retirement in
  // tests/fixtures/former-eager-dependencies.json a false claim.
  feedOrders: Record<string, Record<string, unknown>>;
  // The two SETTERS are still threaded, and are not reads. App's `feedOrders`
  // state is also BroilerFeedView's — the blob carries the three poultry maps in
  // the same object — so a pig order that skipped setFeedOrders would leave
  // /broiler/feed rendering a stale blob for the rest of the session.
  setFeedOrders: Dispatch<SetStateAction<Record<string, Record<string, unknown>>>>;
  pigFeedInventory: PigFeedInventory;
  setPigFeedInventory: Dispatch<SetStateAction<PigFeedInventory>>;
  // Kept declared, but deliberately not destructured: App's integration literal
  // still supplies these historical props and changing it is outside this route
  // seam. No route element below receives them. The former feeder writer is
  // route-local now, and scheduled maintenance owns derived mirror publication.
  // (`persistFeeders` was among them until migration 237 deleted App's dead
  // writer; App no longer supplies it.)
  pigAutoSaveTimer: TimerRef;
  subAutoSaveTimer: TimerRef;
  tripAutoSaveTimer: TimerRef;
  // Historical App UI state, kept declared only because App's integration
  // literal still supplies it. PigBatchesView owns no collapsed-state handoff.
  collapsedBatches: Set<string>;
  setCollapsedBatches: Dispatch<SetStateAction<Set<string>>>;
  collapsedMonths: Set<string>;
  setCollapsedMonths: Dispatch<SetStateAction<Set<string>>>;
  // `persistFarrowing`, `persistBreeders`, `persistBreedOptions`,
  // `persistOriginOptions` and `resolveSire` USED TO BE DECLARED HERE. The four
  // writers were bare `sbSave` calls and are retired; `resolveSire` was an App
  // closure over App's own breedingCycles and boarNames that FarrowingView and
  // SowsView already bound as `_resolveSire` without calling — the
  // retained-but-unused hand-off both declarations said was waiting on this
  // file. App no longer builds it, so there is nothing left to hand over.
};

export function PigEntry({location, setFeedOrders, setPigFeedInventory}: PigEntryProps) {
  const routes = (
    <AppRoutes location={location}>
      {/* No props at all. Both pig daily surfaces own their reads: the list and
          the record row through pig.dailys.list / pig.daily.detail, and the
          feeder directory through pig.dailys.groupDirectory, read in
          PigDailysRouter above the index/record branch — which is exactly where
          this hand-off used to land. */}
      <AppRoute path="/pig/dailys/*" element={<PigDailysView />} />
      {/* Two props, both of them writes. The two blobs that used to travel with
          them are read through pig.feed.core now; handing either back here is
          the prop channel former_eager_evidence_static derives, and it would
          make the `pigs` retirement false. */}
      <AppRoute
        path="/pig/feed"
        element={
          <React.Suspense
            fallback={
              <div role="status" aria-live="polite" style={{padding: '2rem', textAlign: 'center'}}>
                Loading route...
              </div>
            }
          >
            <PigFeedView setFeedOrders={setFeedOrders} setPigFeedInventory={setPigFeedInventory} />
          </React.Suspense>
        }
      />
      <AppRoute path="/pig" element={<PigsHomeView />} />
      {/* No props at all. /pig/breeding owns its three reads through
          pig.breeding.core and its ppp-breeding-v1 write through
          src/lib/appStoreBlobApi.ts, and the 500 ms Gantt debounce handle went
          with the write. */}
      <AppRoute path="/pig/breeding" element={<BreedingView />} />
      {/* Both batch modes own their route-local reads, writes, debounce refs, and
          optimistic state. The App props remain declared above only so its
          untouched legacy integration literal type-checks. */}
      <AppRoute path="/pig/batches/*" element={<PigBatchesView />} />
      <AppRoute path="/pig/farrowing/*" element={<FarrowingView />} />
      {/* No props at all. /pig/sows owns its seven reads through pig.sows.core
          and all three of its app_store writes through
          src/lib/appStoreBlobApi.ts. */}
      <AppRoute path="/pig/sows/*" element={<SowsView />} />
    </AppRoutes>
  );
  // N12: a crash in the routed page stays inside it; App and the header
  // stay mounted (src/app/PageBoundary).
  return <PageBoundary>{routes}</PageBoundary>;
}

export default PigEntry;
